import React from 'react';
import { Link } from 'react-router-dom';
import './EmploiProduct.css';

const EmploiProduct = () => {
  return (
    <div className="emploi-product-page">
      {/* HERO */}
      <section className="emploi-hero">
        <div className="hero-bg-emploi"></div>
        <div className="hero-grid"></div>
        <div className="hero-blob blob1"></div>
        <div className="hero-blob blob2"></div>

        <div className="container">
          <div className="hero-content">
            <div className="section-tag">
              Krishpar's Proprietary Recruitment Engine
            </div>
            <h1>The intelligence<br /><span>behind every<br />placement we make.</span></h1>
            <p className="hero-italic">Redefining How Companies Find and Hire Talent.</p>
            <p className="hero-body">
              Emploi is not a tool we sell — it is the <strong>operational system that runs every Krishpar recruitment engagement.</strong>
              Every candidate sourced, every profile submitted, every placement closed runs through Emploi.
              It's what makes our service structured, fast, and consistently reliable for you.
            </p>

            <div className="hero-actions">
              <a href="#solution" className="btn-primary">See Exactly How We Recruit</a>
              <a href="#clients" className="btn-outline">What You Get as a Client</a>
            </div>

            <div className="proof-stats">
              <div className="stat-item">
                <div className="stat-number">6</div>
                <div className="stat-label">Integrated Modules</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">AI</div>
                <div className="stat-label">Resume Intelligence</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">360°</div>
                <div className="stat-label">Pipeline Visibility</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">0</div>
                <div className="stat-label">Manual Status Emails</div>
              </div>
            </div>
          </div>

          <div className="hero-dashboard">
            <div className="dashboard">
              <div className="dashboard-bar">
                <div className="dot red"></div>
                <div className="dot yellow"></div>
                <div className="dot green"></div>
                <span className="dashboard-tag">Emploi · Live Recruitment Dashboard</span>
              </div>
              <div className="dashboard-body">
                <div className="dashboard-greeting">Good morning, Krishpar Recruitment Team</div>
                <div className="dashboard-title">Today's Hiring Overview</div>
                <div className="kpis">
                  <div className="kpi">
                    <div className="kpi-value">47</div>
                    <div className="kpi-label">Open Roles</div>
                  </div>
                  <div className="kpi">
                    <div className="kpi-value">128</div>
                    <div className="kpi-label">Submitted</div>
                  </div>
                  <div className="kpi">
                    <div className="kpi-value">23</div>
                    <div className="kpi-label">Interviews</div>
                  </div>
                </div>
                <div className="pipeline-header">Live Hiring Pipeline</div>
                <div className="pipeline-rows">
                  <div className="pipeline-row">
                    <span className="row-label">Sourcing</span>
                    <div className="row-track">
                      <div className="row-fill green" style={{ width: '88%' }}></div>
                    </div>
                    <span className="row-number">88</span>
                  </div>
                  <div className="pipeline-row">
                    <span className="row-label">Submitted</span>
                    <div className="row-track">
                      <div className="row-fill blue" style={{ width: '72%' }}></div>
                    </div>
                    <span className="row-number">72</span>
                  </div>
                  <div className="pipeline-row">
                    <span className="row-label">Interview</span>
                    <div className="row-track">
                      <div className="row-fill amber" style={{ width: '54%' }}></div>
                    </div>
                    <span className="row-number">54</span>
                  </div>
                  <div className="pipeline-row">
                    <span className="row-label">Offered</span>
                    <div className="row-track">
                      <div className="row-fill green" style={{ width: '36%' }}></div>
                    </div>
                    <span className="row-number">36</span>
                  </div>
                  <div className="pipeline-row">
                    <span className="row-label">Joined</span>
                    <div className="row-track">
                      <div className="row-fill green" style={{ width: '22%' }}></div>
                    </div>
                    <span className="row-number">22</span>
                  </div>
                </div>
                <div className="ai-status">
                  <div className="ai-icon">⚡</div>
                  <div className="ai-text">
                    <strong>AI Resume Engine Active</strong>
                    14 profiles auto-formatted and ready for submission
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTER SECTION */}
      <section className="chapter-section">
        <div className="container">
          <div className="chapter-grid">
            <div className="chapter-item">
              <div className="chapter-number">Structured</div>
              <div className="chapter-label">Process — every hire, every time</div>
            </div>
            <div className="chapter-item">
              <div className="chapter-number">Automated</div>
              <div className="chapter-label">Admin handled — zero manual work</div>
            </div>
            <div className="chapter-item">
              <div className="chapter-number">Transparent</div>
              <div className="chapter-label">Full pipeline visible to you, live</div>
            </div>
            <div className="chapter-item">
              <div className="chapter-number">Reliable</div>
              <div className="chapter-label">Consistently fast, consistently right</div>
            </div>
          </div>
        </div>
      </section>

      {/* STORY SECTION */}
      <section className="story-section" id="story">
        <div className="container">
          <div className="section-tag" style={{ justifyContent: 'start' }}>Why We Built This</div>
          <h2 className="story-heading">Every hiring problem you've had<br />with agencies — <strong>we've felt it too.</strong></h2>
          <div className="story-grid">
            <div className="story-left">
              <p className="story-text">
                We've been in recruitment long enough to know how it usually works. A job requirement comes in. It gets forwarded to a recruiter on email. Candidates are tracked on a shared sheet that nobody updates the same way. Profiles go out in whatever format the recruiter prefers. A client asks for an update and someone has to piece together a response from three different conversations.
              </p>
              <p className="story-text">
                It's not incompetence — it's the absence of a system. And <strong>without a system, even the best recruiters are fighting one hand behind their back.</strong> Great candidates get lost. Offers drop off. Clients stop trusting the updates they receive. The recruitment service becomes reactive instead of reliable.
              </p>
              <p className="story-text">
                We built Emploi because we refused to operate that way. Not to sell software — but because <strong>our service is only as good as the infrastructure running underneath it.</strong> Emploi is that infrastructure. It gives our team the clarity to act fast, the tools to work precisely, and the visibility to never let anything fall through.
              </p>
              <div className="story-quote">
                <div className="quote-text">"Good recruitment isn't about working harder. It's about working inside a system that never lets the important things slip."</div>
                <div className="quote-attribution">— The philosophy behind Emploi</div>
              </div>
            </div>
            <div className="story-right">
              <div className="pain-header">What happens without a system like this</div>
              <ul className="pain-list">
                <li className="pain-card">
                  <span className="pain-icon">📧</span>
                  <div className="pain-content">
                    <strong>Email as the only record</strong>
                    <span>Candidate status, interview notes, and client feedback scattered across inboxes — no single source of truth.</span>
                  </div>
                </li>
                <li className="pain-card">
                  <span className="pain-icon">📊</span>
                  <div className="pain-content">
                    <strong>Spreadsheets that contradict each other</strong>
                    <span>Each recruiter tracks differently. Consolidating for a client update takes hours and still isn't accurate.</span>
                  </div>
                </li>
                <li className="pain-card">
                  <span className="pain-icon">📄</span>
                  <div className="pain-content">
                    <strong>Inconsistent resume formats</strong>
                    <span>Clients waste screening time on formatting instead of evaluating talent. Conversion suffers.</span>
                  </div>
                </li>
                <li className="pain-card">
                  <span className="pain-icon">🔁</span>
                  <div className="pain-content">
                    <strong>Re-sourcing what you already have</strong>
                    <span>No searchable repository means every new requirement starts from zero — even when the candidate is already in your database.</span>
                  </div>
                </li>
                <li className="pain-card">
                  <span className="pain-icon">💸</span>
                  <div className="pain-content">
                    <strong>Offer drop-offs with no explanation</strong>
                    <span>Candidates back out at the last stage, and there's no system to record why or prevent it from happening again.</span>
                  </div>
                </li>
                <li className="pain-card">
                  <span className="pain-icon">👻</span>
                  <div className="pain-content">
                    <strong>Clients left in the dark</strong>
                    <span>No proactive updates means clients chase you. Trust erodes. Relationships become transactional.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="solution-section" id="solution">
        <div className="container">
          <div className="solution-intro">
            <div className="section-tag" style={{ justifyContent: 'center' }}>How We Recruit</div>
            <h2 style={{ textAlign: 'center' }}>Six stages. One unbroken system.<br /><em>This is exactly how we work.</em></h2>
            <p className="lead">
              Every placement Krishpar makes follows this precise, end-to-end process — powered by Emploi at every step. No gaps. No hand-off failures. No guesswork.
            </p>
          </div>

          <div className="workflow">
            <div className="workflow-spine"></div>

            <div className="workflow-step">
              <div className="workflow-node">
                <div className="workflow-dot">1</div>
              </div>
              <div className="workflow-card">
                <div className="workflow-mod">Stage 01 · Intake</div>
                <div className="workflow-emoji">🎯</div>
                <div className="workflow-title">We start by understanding — precisely.</div>
                <p className="workflow-body">
                  Before a single candidate is sourced, Emploi captures the complete requirement brief. <strong>Skill matrix, experience range, budget, location, engagement model, urgency, role ownership.</strong> Every recruiter on our team is aligned from the first minute. No vague briefs. No mid-search re-briefing. No wasted sourcing effort.
                </p>
                <div className="workflow-tags">
                  <span className="workflow-tag">Permanent</span>
                  <span className="workflow-tag">Contract</span>
                  <span className="workflow-tag">C2C</span>
                  <span className="workflow-tag">C2H</span>
                  <span className="workflow-tag">Clear accountability</span>
                  <span className="workflow-tag">Zero rework</span>
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="workflow-node">
                <div className="workflow-dot">2</div>
              </div>
              <div className="workflow-card">
                <div className="workflow-mod">Stage 02 · AI Intelligence</div>
                <div className="workflow-emoji">🤖</div>
                <div className="workflow-title">Every resume enters raw. Every profile leaves ready.</div>
                <p className="workflow-body">
                  The moment a candidate enters Emploi, our <strong>AI Resume Builder automatically processes their profile</strong> — converting any format into a standardized, role-aligned, client-ready submission. Formatted to your preferences. Highlighted for skills your role demands. Done in minutes, not days, without manual effort from our recruiters.
                </p>
                <div className="workflow-tags">
                  <span className="workflow-tag">Auto-structured</span>
                  <span className="workflow-tag">Role-aligned</span>
                  <span className="workflow-tag">Client-specific templates</span>
                  <span className="workflow-tag">Higher interview rates</span>
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="workflow-node">
                <div className="workflow-dot">3</div>
              </div>
              <div className="workflow-card">
                <div className="workflow-mod">Stage 03 · Talent Repository</div>
                <div className="workflow-emoji">🗄️</div>
                <div className="workflow-title">Every candidate we've ever placed is a permanent asset.</div>
                <p className="workflow-body">
                  Emploi maintains a <strong>searchable, living repository of every candidate</strong> who's ever come through Krishpar. Full history — every submission, every interview, every outcome. When a new requirement comes in, we search our own intelligence first. We've placed candidates from requirements two years apart. That's the value of a system with memory.
                </p>
                <div className="workflow-tags">
                  <span className="workflow-tag">Smart search</span>
                  <span className="workflow-tag">Full history</span>
                  <span className="workflow-tag">Zero re-sourcing</span>
                  <span className="workflow-tag">Long-term ROI</span>
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="workflow-node">
                <div className="workflow-dot">4</div>
              </div>
              <div className="workflow-card">
                <div className="workflow-mod">Stage 04 · Submissions</div>
                <div className="workflow-emoji">📤</div>
                <div className="workflow-title">The right candidate. The right role. Every time.</div>
                <p className="workflow-body">
                  Candidates are <strong>mapped directly to requirements inside Emploi</strong>. Quality benchmarks are enforced before any profile leaves our system. Duplicate submissions are automatically flagged and blocked. Client responses are tracked against every submission. Nothing goes out without accountability. Nothing comes back without a record.
                </p>
                <div className="workflow-tags">
                  <span className="workflow-tag">Quality gates</span>
                  <span className="workflow-tag">No duplicates</span>
                  <span className="workflow-tag">Client response tracked</span>
                  <span className="workflow-tag">Full accountability</span>
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="workflow-node">
                <div className="workflow-dot">5</div>
              </div>
              <div className="workflow-card">
                <div className="workflow-mod">Stage 05 · Interview Coordination</div>
                <div className="workflow-emoji">📅</div>
                <div className="workflow-title">Coordination that doesn't fall through the cracks.</div>
                <p className="workflow-body">
                  Scheduling, confirmations, live status tracking, structured feedback capture — all centralized in Emploi. <strong>Every candidate has a complete, timestamped interview history.</strong> Every client feedback is recorded and structured. No chasing. No forgotten follow-ups. No "I'll check and get back to you." The interview stage is owned, end to end.
                </p>
                <div className="workflow-tags">
                  <span className="workflow-tag">Centralized scheduling</span>
                  <span className="workflow-tag">Live status</span>
                  <span className="workflow-tag">Structured feedback</span>
                  <span className="workflow-tag">Full history</span>
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="workflow-node">
                <div className="workflow-dot">6</div>
              </div>
              <div className="workflow-card">
                <div className="workflow-mod">Stage 06 · Offer, Joining & Closure</div>
                <div className="workflow-emoji">🏁</div>
                <div className="workflow-title">We measure success by joinings — not offers.</div>
                <p className="workflow-body">
                  <strong>Offer release, acceptance, negotiation stages, expected vs actual Date of Joining, and drop-off reasons</strong> — all tracked in Emploi. When a candidate hesitates, we know early and act. When an offer drags, we escalate before it's lost. Our joining ratios are high because we manage the last mile with the same precision we apply to the first.
                </p>
                <div className="workflow-tags">
                  <span className="workflow-tag">Offer tracking</span>
                  <span className="workflow-tag">DOJ management</span>
                  <span className="workflow-tag">Drop-off analysis</span>
                  <span className="workflow-tag">High joining ratios</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI ENGINE SECTION */}
      <section className="ai-section" id="ai">
        <div className="container">
          <div className="section-tag" style={{ justifyContent: 'center' }}>The AI Engine</div>
          <h2>The technology that makes<br />our submissions <strong>convert.</strong></h2>
          <p className="lead">
            Most recruitment agencies send what they receive. We send what clients actually want to screen — structured, relevant, and ready to evaluate from the first line.
          </p>

          <div className="ai-layout">
            <div className="ai-left">
              <p className="ai-body">
                Resume preparation is the silent bottleneck in every recruitment operation. A genuinely strong candidate buried in a poorly formatted, inconsistently structured resume gets overlooked — not because they're the wrong fit, but because <strong>no one had the time or system to present them properly.</strong>
              </p>
              <p className="ai-body">
                Emploi's AI Resume Builder solves this at the system level. Every profile that enters our platform is <strong>automatically processed, reformatted, role-aligned, and enriched</strong> before it ever reaches your inbox. The right skills surface. The relevant experience stands out. The formatting matches your expectations. And it happens in minutes — not manually, not inconsistently.
              </p>

              <ul className="ai-features">
                <li className="ai-feature">
                  <div className="ai-feature-dot"></div>
                  <div>
                    <strong>Consistent format — regardless of how it arrived</strong>
                    <span>Any resume, any format, any level of structure — leaves Emploi clean, professional, and readable every single time.</span>
                  </div>
                </li>
                <li className="ai-feature">
                  <div className="ai-feature-dot"></div>
                  <div>
                    <strong>Role-aligned skill highlighting</strong>
                    <span>The AI reads the requirement and surfaces what matters most for that specific role — not a generic skills list.</span>
                  </div>
                </li>
                <li className="ai-feature">
                  <div className="ai-feature-dot"></div>
                  <div>
                    <strong>Client-specific templates applied automatically</strong>
                    <span>We know what each client prefers. Emploi remembers and applies the right format every time, without being asked.</span>
                  </div>
                </li>
                <li className="ai-feature">
                  <div className="ai-feature-dot"></div>
                  <div>
                    <strong>Instant updates as candidates grow</strong>
                    <span>New certification, new project, new role — updated in Emploi in seconds. No reformatting. No rebuilding.</span>
                  </div>
                </li>
                <li className="ai-feature">
                  <div className="ai-feature-dot"></div>
                  <div>
                    <strong>The direct result: higher interview conversion</strong>
                    <span>Clients screen faster. Interviews happen sooner. More of what we send converts — because more of what we send is genuinely ready.</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="ai-right">
              <div className="resume-card">
                <div className="resume-header bad">⚠ Before Emploi — Raw Resume, As Received</div>
                <div className="resume-body">
                  <div className="resume-line w100"></div>
                  <div className="resume-line w68"></div>
                  <div style={{ height: '8px' }}></div>
                  <div className="resume-line w85"></div>
                  <div className="resume-line w54"></div>
                  <div className="resume-line w92"></div>
                  <div style={{ height: '8px' }}></div>
                  <div className="resume-line w100"></div>
                  <div className="resume-line w78"></div>
                  <div className="resume-line w54"></div>
                  <div className="resume-line w68"></div>
                </div>
              </div>

              <div className="transform">
                <div className="transform-line"></div>
                <div className="transform-badge">⚡</div>
                <div className="transform-line"></div>
                <div className="transform-label">Emploi AI Engine</div>
              </div>

              <div className="resume-card" style={{ marginTop: '18px' }}>
                <div className="resume-header good">✓ After Emploi — Submission-Ready Profile</div>
                <div className="resume-body">
                  <div className="resume-line hi w100"></div>
                  <div className="resume-line hi w85"></div>
                  <div style={{ height: '8px' }}></div>
                  <div className="resume-line hi w92"></div>
                  <div className="resume-line w68"></div>
                  <div className="resume-line hi w85"></div>
                  <div style={{ height: '8px' }}></div>
                  <div className="resume-line hi w100"></div>
                  <div className="resume-line hi w92"></div>
                  <div className="resume-line w54"></div>
                  <div className="resume-line hi w85"></div>
                </div>
              </div>

              <div className="resume-result">
                <strong>The outcome for you</strong>
                Faster screening. Fewer clarification requests. Higher quality interviews. More successful placements — delivered on time, consistently.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENTS SECTION */}
      <section className="clients-section" id="clients">
        <div className="container">
          <div className="clients-intro">
            <div className="section-tag" style={{ justifyContent: 'center' }}>What You Get as a Client</div>
            <h2>When Emploi runs your hiring,<br />you experience something <strong>different.</strong></h2>
            <p className="lead">
              Our internal system translates directly into a better experience for every company we recruit for. Here's what that looks like in practice.
            </p>
          </div>

          <div className="clients-grid">
            <div className="client-card">
              <div className="client-num">01</div>
              <div className="client-icon">📋</div>
              <div className="client-title">Profiles that are ready to screen — not just sent</div>
              <p className="client-body">
                Every submission that reaches you has been AI-processed, role-aligned, and formatted to your expectations. You open it and evaluate talent — not formatting or inconsistency.
              </p>
              <ul className="client-list">
                <li>Consistent format across every submission</li>
                <li>Skills highlighted for your specific role</li>
                <li>Your preferred template applied automatically</li>
                <li>Ready to interview from the first profile you open</li>
              </ul>
            </div>

            <div className="client-card">
              <div className="client-num">02</div>
              <div className="client-icon">👁️</div>
              <div className="client-title">Visibility you can rely on — without chasing us for it</div>
              <p className="client-body">
                You'll always know where every requirement stands. Profile status, interview stages, offer progress — visible and up to date, proactively. No update calls. No email threads asking for a status.
              </p>
              <ul className="client-list">
                <li>Real-time pipeline status per requirement</li>
                <li>Interview schedule and outcome tracking</li>
                <li>Offer and joining progress — always current</li>
                <li>No manual reporting or status meetings needed</li>
              </ul>
            </div>

            <div className="client-card">
              <div className="client-num">03</div>
              <div className="client-icon">⚡</div>
              <div className="client-title">Speed and consistency — not dependent on who's working that day</div>
              <p className="client-body">
                Because our processes live in Emploi — not in someone's head or inbox — quality and pace of delivery is consistent regardless of which recruiter is on your account.
              </p>
              <ul className="client-list">
                <li>Faster sourcing and submission turnaround</li>
                <li>Process continuity — no key-person dependency</li>
                <li>Quality checks before anything reaches you</li>
                <li>Offer-to-joining managed with the same precision</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ANALYTICS SECTION */}
      <section className="analytics-section" id="analytics">
        <div className="container">
          <div className="funnel-intro">
            <div className="section-tag" style={{ justifyContent: 'center' }}>Recruitment Analytics</div>
            <h2>We track every stage.<br /><strong>Nothing is invisible to us.</strong></h2>
            <p className="lead">
              Emploi gives us a live view of the entire hiring funnel — so we catch issues early, improve conversion over time, and deliver with predictable reliability.
            </p>
          </div>

          <div className="funnel-wrap">
            <div className="funnel-row" style={{ width: '60%' }}>
              <div className="funnel-bar fb1">
                <span className="funnel-label">📋 Requirements Raised</span>
                <span className="funnel-value">100%</span>
              </div>
            </div>
            <div className="funnel-arrow">↓</div>
            <div className="funnel-row" style={{ width: '50%' }}>
              <div className="funnel-bar fb2">
                <span className="funnel-label">📤 Profiles Submitted to Clients</span>
                <span className="funnel-value">Tracked</span>
              </div>
            </div>
            <div className="funnel-arrow">↓</div>
            <div className="funnel-row" style={{ width: '40%' }}>
              <div className="funnel-bar fb3">
                <span className="funnel-label">🎙️ Interviews Scheduled & Completed</span>
                <span className="funnel-value">Tracked</span>
              </div>
            </div>
            <div className="funnel-arrow">↓</div>
            <div className="funnel-row" style={{ width: '30%' }}>
              <div className="funnel-bar fb4">
                <span className="funnel-label">✅ Offers Released</span>
                <span className="funnel-value">Tracked</span>
              </div>
            </div>
            <div className="funnel-arrow">↓</div>
            <div className="funnel-row" style={{ width: '20%' }}>
              <div className="funnel-bar fb5">
                <span className="funnel-label">🏁 Candidates Successfully Joined</span>
                <span className="funnel-value">The Goal</span>
              </div>
            </div>
          </div>

          <div className="metrics-row">
            <div className="metric-box">
              <div className="metric-icon">↑</div>
              <div className="metric-label">Submission-to-Interview<br />Conversion Rate</div>
            </div>
            <div className="metric-box">
              <div className="metric-icon">↓</div>
              <div className="metric-label">Time-to-Fill<br />Per Requirement</div>
            </div>
            <div className="metric-box">
              <div className="metric-icon">↑</div>
              <div className="metric-label">Offer-to-Joining<br />Success Rate</div>
            </div>
            <div className="metric-box">
              <div className="metric-icon">360°</div>
              <div className="metric-label">Recruiter & Pipeline<br />Visibility</div>
            </div>
          </div>
        </div>
      </section>

      {/* ROLES SECTION */}
      <section className="roles-section" id="roles">
        <div className="container">
          <div className="roles-intro">
            <div className="section-tag" style={{ justifyContent: 'start' }}>Inside Krishpar</div>
            <h2>How every person on our team<br />uses Emploi <strong>to serve you better.</strong></h2>
            <p className="lead" style={{ justifyContent: 'start', textAlign: 'start', margin: 0 }}>
              Emploi surfaces the right information to the right person on our team — so everyone operates at their best, every day.
            </p>
          </div>

          <div className="roles-layout">
            <ul className="roles-nav">
              <li className="role-nav-item active" data-role="recruiter">👤 Recruiters</li>
              <li className="role-nav-item" data-role="manager">📊 Account Managers</li>
              <li className="role-nav-item" data-role="leadership">🔭 Leadership</li>
              <li className="role-nav-item" data-role="client">🤝 Your View as a Client</li>
            </ul>
            <div className="roles-content">
              {/* Recruiter */}
              <div id="role-recruiter" className="role-panel active">
                <div className="role-title">Less admin. More placements for you.</div>
                <p className="role-desc">
                  Emploi removes the operational weight from our recruiters so they spend their time <strong>sourcing, evaluating, and placing</strong> — not updating spreadsheets, formatting resumes, or composing status emails.
                </p>
                <div className="role-features-grid">
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Clear role ownership
                    </div>
                    <p className="role-feature-desc">Every requirement is assigned with accountability. No ambiguity about who owns what, ever.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      AI handles resume prep
                    </div>
                    <p className="role-feature-desc">Profiles are formatted and aligned automatically. Zero manual formatting time wasted.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Structured daily workflow
                    </div>
                    <p className="role-feature-desc">Submissions, interviews, and follow-ups flow through a single guided system — nothing falls out.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Live performance visibility
                    </div>
                    <p className="role-feature-desc">Every recruiter sees their conversion rates, pipeline health, and daily output — in real time.</p>
                  </div>
                </div>
                <div className="role-metrics-row">
                  <div className="role-metric">
                    <div className="role-metric-value">3×</div>
                    <div className="role-metric-label">Faster Profile Prep</div>
                  </div>
                  <div className="role-metric">
                    <div className="role-metric-value">↑</div>
                    <div className="role-metric-label">Submission Quality</div>
                  </div>
                  <div className="role-metric">
                    <div className="role-metric-value">0</div>
                    <div className="role-metric-label">Manual Status Emails</div>
                  </div>
                </div>
              </div>

              {/* Manager */}
              <div id="role-manager" className="role-panel">
                <div className="role-title">Every client pipeline, always visible.</div>
                <p className="role-desc">
                  Account managers have a live view of every client's hiring status at all times. <strong>No internal chasing. No manual report building.</strong> They walk into every client call already informed.
                </p>
                <div className="role-features-grid">
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Live pipeline per client
                    </div>
                    <p className="role-feature-desc">Full delivery status for every account, updated in real time without manual input.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Delivery metrics
                    </div>
                    <p className="role-feature-desc">Submission-to-interview ratios and fulfilment rates, per client and per role.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Proactive client updates
                    </div>
                    <p className="role-feature-desc">Share accurate, real-time reports without compiling anything manually beforehand.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Bottleneck alerts
                    </div>
                    <p className="role-feature-desc">Spot where a pipeline is stalling before it becomes a delivery failure or a client complaint.</p>
                  </div>
                </div>
                <div className="role-metrics-row">
                  <div className="role-metric">
                    <div className="role-metric-value">Live</div>
                    <div className="role-metric-label">Pipeline Status</div>
                  </div>
                  <div className="role-metric">
                    <div className="role-metric-value">↑</div>
                    <div className="role-metric-label">Client Confidence</div>
                  </div>
                  <div className="role-metric">
                    <div className="role-metric-value">↓</div>
                    <div className="role-metric-label">Manual Reporting</div>
                  </div>
                </div>
              </div>

              {/* Leadership */}
              <div id="role-leadership" className="role-panel">
                <div className="role-title">The whole operation. One view.</div>
                <p className="role-desc">
                  Leadership has macro-level dashboards covering team performance, delivery health, and strategic pipeline data — so every decision about capacity, clients, and growth is <strong>grounded in real numbers.</strong>
                </p>
                <div className="role-features-grid">
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Org-wide dashboards
                    </div>
                    <p className="role-feature-desc">Revenue pipeline, recruiter performance, and delivery health visible in one place.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Recruiter productivity data
                    </div>
                    <p className="role-feature-desc">Objective performance evidence — we coach from facts, not perception or gut feel.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Demand forecasting
                    </div>
                    <p className="role-feature-desc">Pipeline trends that let us plan capacity and anticipate client needs before they escalate.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Complete audit trail
                    </div>
                    <p className="role-feature-desc">Full records for every requirement, candidate, and hiring decision — always, without exception.</p>
                  </div>
                </div>
                <div className="role-metrics-row">
                  <div className="role-metric">
                    <div className="role-metric-value">360°</div>
                    <div className="role-metric-label">Visibility</div>
                  </div>
                  <div className="role-metric">
                    <div className="role-metric-value">Data</div>
                    <div className="role-metric-label">Backed Decisions</div>
                  </div>
                  <div className="role-metric">
                    <div className="role-metric-value">Full</div>
                    <div className="role-metric-label">Audit Trail</div>
                  </div>
                </div>
              </div>

              {/* Client */}
              <div id="role-client" className="role-panel">
                <div className="role-title">You're always in the loop — without asking.</div>
                <p className="role-desc">
                  Emploi makes your hiring pipeline transparent. <strong>Profile status, interview stages, offer progress</strong> — you can see exactly where things stand, at any time, without a single follow-up email or call.
                </p>
                <div className="role-features-grid">
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Real-time candidate status
                    </div>
                    <p className="role-feature-desc">Always know where each submitted profile stands — no chasing required.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Interview visibility
                    </div>
                    <p className="role-feature-desc">Scheduling, feedback, and decision timelines — structured and accessible at all times.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      No email dependency
                    </div>
                    <p className="role-feature-desc">Updates are structured and centralized. You never need to ask where something is.</p>
                  </div>
                  <div className="role-feature">
                    <div className="role-feature-title">
                      <span className="role-feature-dot"></span>
                      Predictable delivery timelines
                    </div>
                    <p className="role-feature-desc">Shared milestones that set clear expectations and build trust consistently over time.</p>
                  </div>
                </div>
                <div className="role-metrics-row">
                  <div className="role-metric">
                    <div className="role-metric-value">Live</div>
                    <div className="role-metric-label">Hiring Status</div>
                  </div>
                  <div className="role-metric">
                    <div className="role-metric-value">↑</div>
                    <div className="role-metric-label">Trust & Retention</div>
                  </div>
                  <div className="role-metric">
                    <div className="role-metric-value">↓</div>
                    <div className="role-metric-label">Follow-up Friction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY SECTION */}
      <section className="why-section" id="why">
        <div className="container">
          <div className="section-tag" style={{ justifyContent: 'center' }}>Why In-House Changes Everything</div>
          <h2>We didn't licence a tool.<br />We <strong>built system</strong> <em>our service depends on.</em></h2>
          <div className="why-grid-emploi">
            <div className="why-cell">
              <div className="why-ghost">01</div>
              <div className="why-icon">🔧</div>
              <div className="why-title">Built around how we actually recruit</div>
              <p className="why-text">Every screen, every workflow, every automation in Emploi mirrors how Krishpar operates. It wasn't adapted from a generic template — it was designed from the ground up for this exact service.</p>
            </div>
            <div className="why-cell">
              <div className="why-ghost">02</div>
              <div className="why-icon">🧠</div>
              <div className="why-title">AI that understands our clients and verticals</div>
              <p className="why-text">Our resume AI isn't producing generic output. It understands our client preferences, our industry verticals, and our submission standards — and applies that context to every profile.</p>
            </div>
            <div className="why-cell">
              <div className="why-ghost">03</div>
              <div className="why-icon">🔗</div>
              <div className="why-title">One system with no integration gaps</div>
              <p className="why-text">Intake to joining — it's all one platform. No data lost between tools. No switching contexts. No hand-off failures. The whole operation runs connected, always.</p>
            </div>
            <div className="why-cell">
              <div className="why-ghost">04</div>
              <div className="why-icon">📊</div>
              <div className="why-title">Every placement makes the next one better</div>
              <p className="why-text">Every hire feeds back into Emploi. Patterns emerge. Conversion rates improve. Our process learns and sharpens over time — not because someone decided to review it, but because the system does it automatically.</p>
            </div>
            <div className="why-cell">
              <div className="why-ghost">05</div>
              <div className="why-icon">⚡</div>
              <div className="why-title">Automation frees our team to be excellent</div>
              <p className="why-text">The repetitive is automated. Resume formatting, status tracking, coordination, client updates — Emploi handles them. Our team's time goes to judgment, relationships, and quality — things that actually differentiate a great placement from a mediocre one.</p>
            </div>
            <div className="why-cell">
              <div className="why-ghost">06</div>
              <div className="why-icon">🌱</div>
              <div className="why-title">Scales without losing quality</div>
              <p className="why-text">Every new client we take on, every recruiter we add, every new requirement type we handle — Emploi absorbs the operational load. We grow without growing pains, and without quality ever dipping.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="cta-section" id="cta">
        <div className="cta-grid-bg"></div>
        <div className="cta-atmosphere"></div>
        <div className="cta-inner">
          <div className="cta-ey">Krishpar Technologies · Powered by Emploi</div>
          <div className="cta-name">EMPLOI</div>
          <div className="cta-italic">Redefining How Companies Find and Hire Talent.</div>
          <p className="cta-body">
            When you hire through Krishpar, Emploi is working behind every step —
            <strong>structuring the brief, enriching profiles, coordinating interviews, tracking offers, and following through to joining.</strong>
            This is what makes our service different. And this is what we'd love to put to work for you.
          </p>
          <div className="cta-btns">
            <Link to="/contact" className="btn-primary" style={{ fontSize: '15px', padding: '15px 34px' }}>Talk to Us</Link>
            {/* <Link to="/contact" className="btn-outline" style={{ fontSize: '15px', padding: '15px 34px', borderColor: 'rgba(255,255,255,.18)' }}>Talk to Our Team</Link> */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmploiProduct;
